import React, { useEffect, useMemo, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import RefreshIcon from '@material-ui/icons/Refresh'

import Widget from '../../components/Widget'
import {
    createBusinessHoursWindow,
    deleteBusinessHoursWindow,
    updateBusinessHoursWindow,
    useManagementBusinessHours,
} from '../../apis'
import { useApiHttpState } from '../../context/ApiContext'
import {
    createIconActionName,
    DestructiveConfirmationDialog,
    FieldValidationError,
    fieldErrorAttributes,
    SectionStateBoundary,
    useSnackbarSectionInteraction,
} from './primitives'

export const WEEKDAY_NAMES = Object.freeze([
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
])

const EMPTY_DRAFT = Object.freeze({ dayOfWeek: '0', startTime: '09:00', endTime: '17:00' })

export const sortBusinessHoursWindows = records => [...(records || [])].sort((left, right) =>
    Number(left.dayOfWeek) - Number(right.dayOfWeek) ||
    String(left.startTime).localeCompare(String(right.startTime)) ||
    String(left.endTime).localeCompare(String(right.endTime)) ||
    Number(left.id) - Number(right.id))

export const businessHoursWindowLabel = window =>
    `${WEEKDAY_NAMES[Number(window.dayOfWeek)] || 'Unknown day'}, ${window.startTime} to ${window.endTime}`

const errorBody = error => {
    const candidate = Array.isArray(error) ? error[0] : error
    if (candidate && candidate.response && candidate.response.data) return candidate.response.data
    if (candidate && candidate.data) return candidate.data
    return candidate || {}
}

const sectionFromHook = management => management.managementSection ||
    (management.versionToken
        ? {
            section: 'businessHours',
            versionToken: management.versionToken,
            records: management.businessHours || management.records || [],
        }
        : null)

const overlapDescription = error => {
    const body = errorBody(error)
    if (body.code !== 'CONFLICT' || !/overlap/i.test(body.message || '')) return ''
    if (!body.conflict) return 'This window overlaps an existing business-hours window.'
    return `This window overlaps ${businessHoursWindowLabel(body.conflict)}.`
}

const BusinessHoursForm = ({ mode, record, draft, fieldErrors, overlapError, pending, onChange, onCancel, onSubmit }) => {
    const formId = mode === 'edit' ? `business-hours-edit-${record.id}` : 'business-hours-create'
    const title = mode === 'edit' ? `Edit ${businessHoursWindowLabel(record)}` : 'Create business-hours window'
    const overlapId = overlapError ? `${formId}-overlap-error` : undefined

    return (
        <form aria-label={title} onSubmit={onSubmit} noValidate>
            <DialogContent>
            <TextField
                select
                SelectProps={{ native: true }}
                fullWidth
                margin="normal"
                id={`${formId}-dayOfWeek`}
                name="dayOfWeek"
                label="Day of week"
                value={draft.dayOfWeek}
                onChange={onChange}
                disabled={pending}
                error={Boolean(fieldErrors.dayOfWeek)}
                inputProps={fieldErrorAttributes({ sectionId: formId, fieldName: 'dayOfWeek', error: fieldErrors.dayOfWeek })}
            >
                {WEEKDAY_NAMES.map((name, index) => <option key={name} value={index}>{name}</option>)}
            </TextField>
            <FieldValidationError sectionId={formId} fieldName="dayOfWeek" error={fieldErrors.dayOfWeek} />
            <TextField
                fullWidth
                margin="normal"
                id={`${formId}-startTime`}
                name="startTime"
                type="time"
                label="Start time"
                value={draft.startTime}
                onChange={onChange}
                disabled={pending}
                error={Boolean(fieldErrors.startTime)}
                InputLabelProps={{ shrink: true }}
                inputProps={fieldErrorAttributes({
                    sectionId: formId,
                    fieldName: 'startTime',
                    error: fieldErrors.startTime,
                    describedBy: overlapId,
                })}
            />
            <FieldValidationError sectionId={formId} fieldName="startTime" error={fieldErrors.startTime} />
            <TextField
                fullWidth
                margin="normal"
                id={`${formId}-endTime`}
                name="endTime"
                type="time"
                label="End time"
                value={draft.endTime}
                onChange={onChange}
                disabled={pending}
                error={Boolean(fieldErrors.endTime)}
                InputLabelProps={{ shrink: true }}
                inputProps={fieldErrorAttributes({
                    sectionId: formId,
                    fieldName: 'endTime',
                    error: fieldErrors.endTime,
                    describedBy: overlapId,
                })}
            />
            <FieldValidationError sectionId={formId} fieldName="endTime" error={fieldErrors.endTime} />
            {!!overlapError && <p id={overlapId} role="alert">{`Error: ${overlapError}`}</p>}
            </DialogContent>
            <DialogActions>
            <Button type="submit" color="primary" variant="contained" disabled={pending}>
                {mode === 'edit' ? 'Save window' : 'Create window'}
            </Button>
            <Button type="button" onClick={onCancel} disabled={pending}>Cancel</Button>
            </DialogActions>
        </form>
    )
}

const BusinessHoursTable = ({ records, disabled, onEdit, onDelete }) => (
    <Table size="small" aria-label="Business hours windows">
        <TableHead>
            <TableRow>
                <TableCell component="th" scope="col">Day</TableCell>
                <TableCell component="th" scope="col">Start time</TableCell>
                <TableCell component="th" scope="col">End time</TableCell>
                <TableCell component="th" scope="col" align="right">Actions</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {sortBusinessHoursWindows(records).map(window => {
                const label = businessHoursWindowLabel(window)
                return (
                    <TableRow key={window.id}>
                        <TableCell component="th" scope="row">{WEEKDAY_NAMES[Number(window.dayOfWeek)]}</TableCell>
                        <TableCell>{window.startTime}</TableCell>
                        <TableCell>{window.endTime}</TableCell>
                        <TableCell align="right">
                            <IconButton
                                type="button"
                                aria-label={createIconActionName('Edit', label)}
                                onClick={() => onEdit(window)}
                                disabled={disabled}
                            >
                                <EditIcon aria-hidden="true" />
                            </IconButton>
                            <IconButton
                                type="button"
                                aria-label={createIconActionName('Delete', label)}
                                onClick={event => onDelete(window, event.currentTarget)}
                                disabled={disabled}
                            >
                                <DeleteIcon aria-hidden="true" />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                )
            })}
        </TableBody>
    </Table>
)

const mutationPayload = draft => ({
    dayOfWeek: Number(draft.dayOfWeek),
    startTime: draft.startTime,
    endTime: draft.endTime,
})

export default function BusinessHoursSection() {
    const management = useManagementBusinessHours()
    const { api } = useApiHttpState()
    const hookSection = sectionFromHook(management)
    const interaction = useSnackbarSectionInteraction(hookSection)
    const [editor, setEditor] = useState(null)
    const [overlapError, setOverlapError] = useState('')
    const [deleteTarget, setDeleteTarget] = useState(null)
    const headingRef = useRef(null)
    const deleteOpenerRef = useRef(null)
    const observedSectionRef = useRef(hookSection)
    const observedErrorRef = useRef(null)

    useEffect(() => {
        if (!hookSection || observedSectionRef.current === hookSection) return
        observedSectionRef.current = hookSection
        interaction.load(() => hookSection)
    }, [hookSection, interaction.load])

    useEffect(() => {
        if (!management.isError || observedErrorRef.current === management.isError || interaction.state.confirmedData) return
        observedErrorRef.current = management.isError
        interaction.load(() => Promise.reject(management.isError))
    }, [management.isError, interaction.load, interaction.state.confirmedData])

    const records = useMemo(
        () => sortBusinessHoursWindows(interaction.state.confirmedData && interaction.state.confirmedData.records),
        [interaction.state.confirmedData],
    )
    const draft = interaction.state.draft || EMPTY_DRAFT
    const refreshSection = () => management.revalidate()

    const openCreate = () => {
        setEditor({ mode: 'create', record: null })
        setOverlapError('')
        interaction.clearFeedback()
        interaction.setDraft({ ...EMPTY_DRAFT })
    }

    const openEdit = record => {
        setEditor({ mode: 'edit', record })
        setOverlapError('')
        interaction.clearFeedback()
        interaction.setDraft({
            dayOfWeek: String(record.dayOfWeek),
            startTime: record.startTime,
            endTime: record.endTime,
        })
    }

    const closeEditor = () => {
        setEditor(null)
        setOverlapError('')
        interaction.clearFeedback()
        interaction.setDraft(null)
    }

    const updateDraft = event => {
        setOverlapError('')
        interaction.setDraft({ ...draft, [event.target.name]: event.target.value })
    }

    const submitEditor = async event => {
        event.preventDefault()
        if (!editor) return
        const editing = editor.mode === 'edit'
        const payload = mutationPayload(draft)
        const versionToken = interaction.state.confirmedData.versionToken
        const result = await interaction.submit({
            mutation: () => editing
                ? updateBusinessHoursWindow(api, editor.record.id, payload, versionToken)
                : createBusinessHoursWindow(api, payload, versionToken),
            refresh: refreshSection,
            draft: { ...draft },
            mutationKey: editing ? `edit-${editor.record.id}` : 'create',
            successMessage: editing ? 'Business-hours window updated.' : 'Business-hours window created.',
        })
        if (result.ok) {
            setEditor(null)
            setOverlapError('')
        } else if (!result.ignored) {
            setOverlapError(overlapDescription(result.error))
        }
    }

    const requestDelete = (record, opener) => {
        deleteOpenerRef.current = opener
        setDeleteTarget(record)
    }

    const confirmDelete = () => interaction.submit({
        mutation: () => deleteBusinessHoursWindow(
            api,
            deleteTarget.id,
            interaction.state.confirmedData.versionToken,
        ),
        refresh: refreshSection,
        draft: interaction.state.draft,
        mutationKey: `delete-${deleteTarget.id}`,
        successMessage: 'Business-hours window deleted.',
        retainDraft: true,
    })

    const retryLoad = () => interaction.load(refreshSection)
    const retryRefresh = () => interaction.refresh(refreshSection)
    const mutationDisabled = !interaction.canSubmit || interaction.isPending
    const finalWindow = records.length === 1

    return (
        <section aria-labelledby="business-hours-heading">
            <Widget
                disableWidgetMenu
                header={
                    <React.Fragment>
                        <Typography
                            ref={headingRef}
                            id="business-hours-heading"
                            component="h2"
                            variant="h5"
                            color="textSecondary"
                            tabIndex={-1}
                        >
                            Business Hours
                        </Typography>
                        <div>
                            <IconButton
                                type="button"
                                aria-label="Refresh Business Hours"
                                onClick={retryRefresh}
                                disabled={interaction.isPending || !interaction.state.confirmedData}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                type="button"
                                color="primary"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={openCreate}
                                disabled={mutationDisabled || !!editor}
                            >
                                Add business-hours window
                            </Button>
                        </div>
                    </React.Fragment>
                }
            >
                <SectionStateBoundary
                    state={interaction.state}
                    sectionName="business hours"
                    emptyMessage="No business-hours windows are configured. Reminder sends are currently permitted at any time."
                    onRetryLoad={retryLoad}
                    onRetryRefresh={retryRefresh}
                    onAcceptStale={interaction.acceptStale}
                >
                    {() => (
                        <BusinessHoursTable
                            records={records}
                            disabled={mutationDisabled}
                            onEdit={openEdit}
                            onDelete={requestDelete}
                        />
                    )}
                </SectionStateBoundary>
                {!!editor && (
                    <Dialog
                        open
                        onClose={interaction.isPending ? undefined : closeEditor}
                        aria-labelledby="business-hours-editor-title"
                        fullWidth
                        maxWidth="sm"
                    >
                        <DialogTitle id="business-hours-editor-title">
                            {editor.mode === 'edit'
                                ? `Edit ${businessHoursWindowLabel(editor.record)}`
                                : 'Add business-hours window'}
                        </DialogTitle>
                        <BusinessHoursForm
                            mode={editor.mode}
                            record={editor.record}
                            draft={draft}
                            fieldErrors={interaction.state.fieldErrors}
                            overlapError={overlapError}
                            pending={interaction.isPending}
                            onChange={updateDraft}
                            onCancel={closeEditor}
                            onSubmit={submitEditor}
                        />
                    </Dialog>
                )}
            </Widget>
            <DestructiveConfirmationDialog
                open={!!deleteTarget}
                title="Delete business-hours window?"
                recordLabel={deleteTarget ? businessHoursWindowLabel(deleteTarget) : 'Business-hours window'}
                consequence={finalWindow
                    ? 'Deleting this final window leaves the schedule empty and permits reminder sends at any time.'
                    : 'Reminder sends will no longer be permitted during this window unless another window applies.'}
                confirmLabel="Delete window"
                onConfirm={confirmDelete}
                onClose={() => setDeleteTarget(null)}
                openerRef={deleteOpenerRef}
                sectionHeadingRef={headingRef}
            />
        </section>
    )
}
